<template>
  <v-dialog v-model="open" persistent scrollable max-width="1200px">
    <v-card>
      <v-toolbar color="surface-variant" density="compact">
        <v-toolbar-title>{{ $t('user-profile.page.title') }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="open = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-alert v-if="errorMessage" type="warning" class="mb-4">{{ errorMessage }}</v-alert>
        <v-alert v-if="successMessage" type="success" class="mb-4">{{ successMessage }}</v-alert>

        <div class="d-flex" v-if="profile">
          <v-tabs v-model="activeTab" direction="vertical">
            <v-tab value="basicData">
              <v-icon start>mdi-account</v-icon>
              {{ $t('user-profile.tab.basicData') }}
            </v-tab>
            <v-tab value="addresses">
              <v-icon start>mdi-map-marker-outline</v-icon>
              {{ $t('user-profile.tab.addresses') }}
            </v-tab>
          </v-tabs>

          <v-tabs-window v-model="activeTab" class="flex-grow-1">

            <!-- Basic data tab -->
            <v-tabs-window-item value="basicData">
              <v-form ref="basicDataForm" @submit.prevent="saveProfile">
                <v-container>
                  <v-row>
                    <v-col cols="12" sm="8">
                      <v-text-field
                        v-model="editedProfile.email"
                        :label="$t('admin-users.user.email')"
                        :error-messages="emailErrors"
                        @blur="v$.email.$touch()"
                      />
                      <v-text-field
                        v-model="editedProfile.firstName"
                        :label="$t('admin-users.user.firstName')"
                        :error-messages="firstNameErrors"
                        @blur="v$.firstName.$touch()"
                      />
                      <v-text-field
                        v-model="editedProfile.lastName"
                        :label="$t('admin-users.user.lastName')"
                        :error-messages="lastNameErrors"
                        @blur="v$.lastName.$touch()"
                      />
                      <v-select
                        v-model="editedProfile.langKey"
                        :items="langOptions"
                        :label="$t('admin-users.user.lang')"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="auto">
                      <v-btn
                        type="submit"
                        variant="flat"
                        color="primary"
                        :disabled="v$.$invalid"
                      >
                        {{ $t('user-profile.save') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-container>
              </v-form>
            </v-tabs-window-item>

            <!-- Addresses tab -->
            <v-tabs-window-item value="addresses">
              <v-container fluid>
                <v-row justify="end" class="mb-2">
                  <v-col cols="auto">
                    <v-btn icon size="small" color="primary" @click="addAddress">
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="addressTableHeaders"
                  :items="addresses"
                  :items-per-page="-1"
                  hide-default-footer
                  density="compact"
                >
                  <template v-slot:item.type="{ item }">
                    {{ $t(`global.form.address.type.${item.type.toLowerCase()}`) }}
                  </template>
                  <template v-slot:item.name="{ item }">
                    <div class="address-location">
                      <span>{{ item.name }}</span>
                      <span v-if="item.taxId" class="text-medium-emphasis text-body-2">({{ $t('global.form.address.taxId.label') }}: {{ item.taxId }})</span>
                    </div>
                  </template>
                  <template v-slot:item.location="{ item }">
                    <div class="address-location">
                      <span v-if="item.addressLine1">{{ item.addressLine1 }}</span>
                      <span v-if="item.addressLine2">{{ item.addressLine2 }}</span>
                      <span v-if="item.zip || item.city">{{ [item.zip, item.city].filter(Boolean).join(' ') }}</span>
                      <span v-if="item.country">{{ item.country }}</span>
                    </div>
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <v-btn icon size="x-small" variant="text" @click="openAddressEditDialog(item)" :title="$t('global.form.address.edit')">
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn icon size="x-small" variant="text" @click="cloneAddress(item)" :title="$t('global.form.address.clone')">
                      <v-icon>mdi-content-duplicate</v-icon>
                    </v-btn>
                    <v-btn icon size="x-small" variant="text" @click="deleteAddress(item)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <template v-slot:no-data>
                    <span class="text-medium-emphasis text-body-2">{{ $t('global.form.address.noAddresses') }}</span>
                  </template>
                </v-data-table>

                <!-- Address edit dialog -->
                <v-dialog v-model="addressDialog" persistent scrollable max-width="700px">
                  <v-form v-if="editedAddress" ref="addressFormRef" @submit.prevent="saveAddress">
                    <v-card>
                      <v-toolbar color="surface-variant" density="compact">
                        <v-toolbar-title>{{ editedAddress.id ? $t('global.form.address.edit') : $t('global.form.address.add') }}</v-toolbar-title>
                        <v-spacer />
                        <v-btn icon @click="closeAddressDialog">
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </v-toolbar>
                      <v-card-text class="d-flex flex-column ga-2">
                        <v-select
                          v-model="editedAddress.type"
                          :items="addressTypeOptions"
                          item-title="title"
                          item-value="value"
                          :label="$t('global.form.address.type')"
                          density="compact"
                          :rules="[v => !!v || $t('admin-validations.required')]"
                        />
                        <v-text-field v-model="editedAddress.name" clearable :label="$t('global.form.address.name')" density="compact" :rules="[v => !!v || $t('global.form.address.name.required')]" />
                        <v-text-field v-model="editedAddress.taxId" clearable :label="$t('global.form.address.taxId')" density="compact" />
                        <v-text-field v-model="editedAddress.country" clearable :label="$t('global.form.address.country')" density="compact" :rules="[v => !!v || $t('admin-validations.required')]" />
                        <v-text-field v-model="editedAddress.state" clearable :label="$t('global.form.address.state')" density="compact" :rules="[v => !!v || $t('admin-validations.required')]" />
                        <v-text-field v-model="editedAddress.zip" clearable :label="$t('global.form.address.zip')" density="compact" :rules="[v => !!v || $t('admin-validations.required')]" />
                        <v-text-field v-model="editedAddress.city" clearable :label="$t('global.form.address.city')" density="compact" :rules="[v => !!v || $t('admin-validations.required')]" />
                        <v-text-field v-model="editedAddress.addressLine1" clearable :label="$t('global.form.address.line1')" density="compact" :rules="[v => !!v || $t('admin-validations.required')]" />
                        <v-text-field v-model="editedAddress.addressLine2" clearable :label="$t('global.form.address.line2')" density="compact" />
                        <v-text-field v-model="editedAddress.phone" clearable :label="$t('global.form.address.phone')" density="compact" :rules="[v => !!v || $t('admin-validations.required')]" />
                        <v-text-field
                          v-model="editedAddress.email"
                          clearable
                          :label="$t('global.form.address.email')"
                          density="compact"
                          :rules="[v => !!v || $t('admin-validations.required'), v => !v || /.+@.+\..+/.test(v) || $t('global.form.address.email.invalid')]"
                        />
                      </v-card-text>
                      <v-card-actions>
                        <v-spacer />
                        <v-btn variant="outlined" @click="closeAddressDialog">{{ $t('action.cancel') }}</v-btn>
                        <v-btn type="submit" variant="flat" color="primary">{{ $t('user-profile.save') }}</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-form>
                </v-dialog>

                <confirm-dialog ref="confirmDialog" />
              </v-container>
            </v-tabs-window-item>

          </v-tabs-window>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, inject, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { email, maxLength, required } from '@vuelidate/validators';
import { useI18n } from '@/shared/composables/i18n';
import type { UserProfileAddressDto, UserProfileResponse, UserProfileUpdateRequest } from '@/venit/services/model/techpubs-cms';
import { RetailUserAddressType } from '@/venit/services/model/techpubs-cms';
import UserProfileService from '@/venit/user/profile/user-profile.service';
import ConfirmDialog from '@/venit/components/confirm-dialog/confirm-dialog.vue';
import { useAccountStore } from '@/stores/account';

const open = defineModel<boolean>({ default: false });

const { t } = useI18n();
const userProfileService = inject<() => UserProfileService>('userProfileService')!;
const accountStore = useAccountStore();
const confirmDialog = ref<InstanceType<typeof ConfirmDialog> | null>(null);
const addressFormRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);

const profile = ref<UserProfileResponse | null>(null);
const activeTab = ref('basicData');
const errorMessage = ref<string | null>(null);
const successMessage = ref<string | null>(null);

const editedProfile = ref<UserProfileUpdateRequest>({ firstName: '', lastName: '', email: '', langKey: 'en' });

const validationRules = {
  email: { required, email },
  firstName: { maxLength: maxLength(50) },
  lastName: { maxLength: maxLength(50) },
};
const v$ = useVuelidate(validationRules, editedProfile);

const emailErrors = computed(() => {
  if (!v$.value.email.$dirty) return [];
  const errors: string[] = [];
  if (v$.value.email.required.$invalid) errors.push(t('admin-users.validation.email'));
  if (v$.value.email.email.$invalid) errors.push(t('admin-users.validation.email'));
  return errors;
});

const firstNameErrors = computed(() => {
  if (!v$.value.firstName.$dirty) return [];
  return v$.value.firstName.maxLength.$invalid ? [t('admin-users.validation.firstName')] : [];
});

const lastNameErrors = computed(() => {
  if (!v$.value.lastName.$dirty) return [];
  return v$.value.lastName.maxLength.$invalid ? [t('admin-users.validation.lastName')] : [];
});

const langOptions = ['pl', 'en'];

const loadProfile = () => {
  userProfileService()
    .getProfile()
    .then(response => {
      profile.value = response.data;
      editedProfile.value = {
        firstName: response.data.firstName ?? '',
        lastName: response.data.lastName ?? '',
        email: response.data.email,
        langKey: response.data.langKey ?? 'en',
      };
    });
};

const saveProfile = async () => {
  await v$.value.$touch();
  if (v$.value.$invalid) return;
  errorMessage.value = null;
  successMessage.value = null;
  userProfileService()
    .updateProfile(editedProfile.value)
    .then(response => {
      profile.value = response.data;
      successMessage.value = t('user-profile.saveSuccess');
    })
    .catch(() => {
      errorMessage.value = t('user-profile.saveError');
    });
};

// Addresses
const addresses = ref<UserProfileAddressDto[]>([]);
const addressDialog = ref(false);
const editedAddress = ref<UserProfileAddressDto | null>(null);

const addressTypeOrder = [RetailUserAddressType.SHIPPING, RetailUserAddressType.BILLING];

const addressTypeOptions = computed(() => addressTypeOrder.map(type => ({
  value: type,
  title: t(`global.form.address.type.${type.toLowerCase()}`),
})));

const addressTableHeaders = computed(() => [
  { title: t('global.form.address.type'), key: 'type', sortable: false },
  { title: t('admin-organization.table.header.name'), key: 'name', sortable: false },
  { title: t('global.form.address.table.location'), key: 'location', sortable: false },
  { title: t('admin-organization.table.header.phone'), key: 'phone', sortable: false },
  { title: t('global.form.address.email'), key: 'email', sortable: false },
  { title: t('global.form.address.table.actions'), key: 'actions', sortable: false, align: 'end' as const },
]);

const loadAddresses = () => {
  userProfileService()
    .listAddresses()
    .then(response => {
      addresses.value = response.data.addresses;
    });
};

const addAddress = () => {
  editedAddress.value = {
    id: null, version: null, type: null,
    name: null, taxId: null, country: null, state: null,
    zip: null, city: null, addressLine1: null, addressLine2: null,
    phone: null, email: editedProfile.value.email ?? null,
  };
  addressDialog.value = true;
};

const openAddressEditDialog = (address: UserProfileAddressDto) => {
  editedAddress.value = { ...address };
  addressDialog.value = true;
};

const cloneAddress = (address: UserProfileAddressDto) => {
  editedAddress.value = { ...address, id: null, version: null };
  addressDialog.value = true;
};

const closeAddressDialog = () => {
  addressDialog.value = false;
  editedAddress.value = null;
};

const saveAddress = async () => {
  const { valid } = await addressFormRef.value!.validate();
  if (!valid) return;
  userProfileService()
    .saveAddress({ address: editedAddress.value })
    .then(response => {
      const saved = response.data.address;
      const idx = addresses.value.findIndex(a => a.id === saved.id);
      if (idx >= 0) {
        addresses.value[idx] = saved;
      } else {
        addresses.value.push(saved);
      }
      closeAddressDialog();
      accountStore.loadUser();
    });
};

const deleteAddress = async (address: UserProfileAddressDto) => {
  if (await confirmDialog.value?.openWithMessage(t('confirm-dialog.deleteMessage').toString())) {
    userProfileService()
      .deleteAddress({ addressId: address.id })
      .then(() => {
        loadAddresses();
        accountStore.loadUser();
      });
  }
};

watch(open, (isOpen) => {
  if (isOpen) {
    activeTab.value = 'basicData';
    errorMessage.value = null;
    successMessage.value = null;
    loadProfile();
    loadAddresses();
  }
});
</script>

<style scoped>
.v-tabs.v-tabs--vertical .v-tab {
  justify-content: left;
}

.address-location {
  display: flex;
  flex-direction: column;
  padding: 4px 0;
  line-height: 1.4;
}
</style>
